import {BrowserModule} from '@angular/platform-browser';
import {NgModule} from '@angular/core';
import {AppComponent} from './app.component';
import {CommonModule, LocationStrategy, PathLocationStrategy} from '@angular/common';
import {HttpClientModule, HTTP_INTERCEPTORS} from '@angular/common/http';
import {ReactiveFormsModule, FormsModule} from '@angular/forms';
import {MaterialModule} from './modules/material.module';
import {RoutingModule} from './modules/routing.module';
import {MainComponent} from './components/landing/main/main.component';
import {LoginComponent} from './components/application/login/login.component';
import {TemplateComponent} from './components/landing/template/template.component';
import {MenuComponent} from './components/common/menu/menu.component';
import {FadPersonalComponent} from './components/landing/fad-personal/fad-personal.component';
import {FadEmpresaComponent} from './components/landing/fad-empresa/fad-empresa.component';
import {FormComponent} from './components/common/dialogs/form/form.component';
import {FormTemplateComponent} from './components/common/dialogs/form-template/form-template.component';
import {FormCompanyTemplateComponent} from './components/common/dialogs/form-company-template/form-company-template.component';
import {MAT_FORM_FIELD_DEFAULT_OPTIONS} from '@angular/material/form-field';
import {MAT_DATE_LOCALE, MAT_DATE_FORMATS, DateAdapter} from '@angular/material/core';
import {MAT_MOMENT_DATE_FORMATS, MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS} from '@angular/material-moment-adapter';
import {FaqsComponent} from './components/landing/faqs/faqs.component';
import {SoporteComponent} from './components/landing/soporte/soporte.component';
import {CreateUserComponent} from './components/landing/create-user/create-user.component';
import {AppInterceptor} from 'src/app/services/interceptors/app-interceptor';
import {PrivacyNoticeComponent} from './components/landing/privacy-notice/privacy-notice.component';
import {CumplimientoJuridicoComponent} from './components/landing/cumplimiento-juridico/cumplimiento-juridico.component';
import {VideoFrameComponent} from './components/common/dialogs/video-frame/video-frame.component';
import {PaymentComponent} from './components/application/payment/payment.component';
import {CheckPaymentComponent} from './components/common/check-payment/check-payment.component';
import {FillPipe} from './common/pipes/fill.pipe';
import {CreateAccountComponent} from './components/application/payment/create-account/create-account.component';
import {PaymentMethodComponent} from './components/application/payment/payment-method/payment-method.component';
import {BillingInfoComponent} from './components/application/payment/billing-info/billing-info.component';
import {PromotionalCodeComponent} from './components/common/promotional-code/promotional-code.component';
import {PurchaseSummaryComponent} from './components/application/payment/purchase-summary/purchase-summary.component';
import {ConfirmationDialogComponent} from './components/common/confirmation-dialog/confirmation-dialog.component';
import {CountActivePipe} from './common/pipes/count-active.pipe';
import {NotificationDialogComponent} from './components/common/notification-dialog/notification-dialog.component';
import {AddComplementsComponent} from './components/common/add-complements/add-complements.component';
import {MenuMovilComponent} from './components/common/dialogs/menu/menu.component';
import {CommonDirective} from './components/common.directive';
import {OnlyLettersDirective} from './common/directives/only-letters.directive';
import {ToUppercaseDirective} from './common/directives/to-uppercase.directive';
import {OnlyNumbersDirective} from './common/directives/only-numbers.directive';
import {OnlyDatesDirective} from './common/directives/only-dates.directive';
import {FormatoMilPipe} from './common/pipes/formato-mil.pipe';
import {PromofirmasComponent } from './components/common/dialogs/promofirmas/promofirmas.component';
import {RECAPTCHA_SETTINGS, RecaptchaModule, RecaptchaSettings} from 'ng-recaptcha';
import { ToLowercaseDirective } from './common/directives/to-lowercase.directive';
import { ThanksPageComponent } from './components/landing/thanks-page/thanks-page.component';
import { DescargaCumplimientoComponent } from './components/landing/descarga-cumplimiento/descarga-cumplimiento.component';
import { CodePromoDirective } from './common/directives/code-promo.directive';
import {CookieService} from 'ngx-cookie-service';
import { TermsConditionsComponent } from './components/landing/terms-conditions/terms-conditions.component';
import { SecurityInformationComponent } from './components/landing/security-information/security-information.component';
import { RegistroComponent } from './components/landing/create-user/registro/registro.component';
import { SmsComponent } from './components/landing/create-user/sms/sms.component';


const globalSettings: RecaptchaSettings = { siteKey: '6LeuyKEaAAAAANjGP1KvvvVasAMDzKCkGmHD4fqh'};

@NgModule({
    declarations: [
        AppComponent,
        MainComponent,
        LoginComponent,
        TemplateComponent,
        MenuComponent,
        FadPersonalComponent,
        FadEmpresaComponent,
        FormComponent,
        FormTemplateComponent,
        FormCompanyTemplateComponent,
        FaqsComponent,
        SoporteComponent,
        CreateUserComponent,
        PrivacyNoticeComponent,
        CumplimientoJuridicoComponent,
        VideoFrameComponent,
        PaymentComponent,
        CheckPaymentComponent,
        FillPipe,
        CreateAccountComponent,
        PaymentMethodComponent,
        BillingInfoComponent,
        PromotionalCodeComponent,
        PurchaseSummaryComponent,
        ConfirmationDialogComponent,
        CountActivePipe,
        NotificationDialogComponent,
        AddComplementsComponent,
        CommonDirective,
        OnlyNumbersDirective,
        OnlyLettersDirective,
        ToUppercaseDirective,
        OnlyDatesDirective,
        MenuMovilComponent,
        FormatoMilPipe,
        PromofirmasComponent,
        ToLowercaseDirective,
        ThanksPageComponent,
        DescargaCumplimientoComponent,
        CodePromoDirective,
        TermsConditionsComponent,
        SecurityInformationComponent,
        RegistroComponent,
        SmsComponent
    ],
    imports: [
        BrowserModule,
        CommonModule,
        HttpClientModule,
        ReactiveFormsModule,
        FormsModule,
        MaterialModule,
        RoutingModule,
        RecaptchaModule
    ],
    entryComponents: [
        FormComponent,
        ConfirmationDialogComponent,
        AddComplementsComponent
    ],
    providers: [
        CookieService,
        {provide: LocationStrategy, useClass: PathLocationStrategy},
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {floatLabel: 'always'}
        },
        {
            provide: MAT_DATE_LOCALE,
            useValue: 'es-MX'
        },
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: MAT_MOMENT_DATE_FORMATS
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AppInterceptor,
            multi: true
        },
        {
            provide: RECAPTCHA_SETTINGS,
            useValue: globalSettings,
        }
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}
